import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { localizedPath, stripLocale } from "@/lib/locale";
import type { LandingLang } from "@/lib/i18n-landing";

interface SEOProps {
  /** `<title>` de la página. Conviene incluir " — Playium" al final, pero
   *  no lo añadimos automáticamente para que cada spoke pueda decidir
   *  el orden (ej. "Pricing — Playium" vs "Playium — Pricing"). */
  title: string;
  /** `<meta name="description">`. ≤ 160 caracteres para no truncar en SERP. */
  description: string;
  /** Path canónico (ej. "/pricing"). Se concatena al SITE_ORIGIN para
   *  formar la URL absoluta del `<link rel="canonical">` y del `og:url`. */
  path: string;
  /** Datos estructurados Schema.org (objeto JS); se serializa como JSON-LD
   *  en un `<script type="application/ld+json">`. Opcional. */
  structuredData?: unknown;
  /** Tipo OG (default "website"). */
  ogType?: "website" | "article" | "product";
  /** Imagen OG absoluta. Si no se pasa, fallback al PWA 512. */
  ogImage?: string;
}

// Origen canónico del sitio público. Cuando exista dominio definitivo se
// cambia aquí. Si queremos per-entorno, se puede leer de
// `import.meta.env.VITE_SITE_ORIGIN`.
const SITE_ORIGIN = "https://www.playium.es";

// Componente reutilizable que monta meta tags por página. Se apoya en
// `react-helmet-async` v2 (HelmetProvider vive en App.tsx).
//
// IMPORTANTE: usamos la API con children (`<Helmet><title>...</title>...
// </Helmet>`). La v3 dropeó esto a favor de props — si alguna vez subimos
// a v3 hay que reescribir todo. Por eso pinneamos a ^2.0.5.
//
// Lo que NO emite todavía:
//   - hreflang: requiere URLs prefijadas por locale (`/es/pricing`,
//     `/ca/pricing`, `/en/pricing`). Ese refactor está diferido — ver
//     docs/seo-multilingue.md en su día.
//   - og:locale_alternate: idem.
export default function SEO({
  title,
  description,
  path,
  structuredData,
  ogType = "website",
  ogImage,
}: SEOProps) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "es") as LandingLang;
  // El `path` que pasan las páginas es el ES base (sin prefijo de idioma). El
  // canónico apunta a la variante del idioma ACTUAL; los hreflang (más abajo)
  // listan las 3 variantes + x-default.
  const basePath = stripLocale(path);
  const canonical = `${SITE_ORIGIN}${localizedPath(basePath, lang)}`;
  const image = ogImage || `${SITE_ORIGIN}/og-image.png`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {/* hreflang: una URL por idioma + x-default (ES). Apunta a las URLs por
          locale (/, /ca, /en) que monta App.tsx. */}
      <link rel="alternate" hrefLang="es" href={`${SITE_ORIGIN}${localizedPath(basePath, "es")}`} />
      <link rel="alternate" hrefLang="ca" href={`${SITE_ORIGIN}${localizedPath(basePath, "ca")}`} />
      <link rel="alternate" hrefLang="en" href={`${SITE_ORIGIN}${localizedPath(basePath, "en")}`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}${basePath}`} />

      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Playium" />
      <meta property="og:locale" content={ogLocale(lang)} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

function ogLocale(lang: string): string {
  if (lang === "ca") return "ca_ES";
  if (lang === "en") return "en_US";
  return "es_ES";
}

/** Snippet sitewide `Organization`. Lo monta App.tsx una vez. */
// eslint-disable-next-line react-refresh/only-export-components -- dato estático que comparte archivo con SEO
export const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Playium",
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/pwa-512x512.png`,
  description:
    "Playium is the operating system for indoor play centers — timed sessions, café, time passes, events, memberships, family app, online payments and offline operation in one platform.",
  sameAs: [],
};
